import { Add } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import classNames from 'classnames';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { OrganizationUnit } from '~/data/orgunits/types';
import { WorkflowApprovalStepConfig, WorkflowConfig } from '~/data/workflows';
import { WorkflowEditProps } from '~/scenes/AdminModule/components/Views/WorkflowsConfigurationView/components/WorkflowConfigurationsTable/component';
import {
  WorkflowProcessConfigState,
  WorkflowRoles,
  getOrgUnitLabel,
  getWorkflowRolesTranslationKeyFromString,
} from '~/scenes/AdminModule/components/Views/WorkflowsConfigurationView/data';

import { useSxStyles } from './useStyles';

type PropsType = {
  workflowConfig: WorkflowConfig;
  workflowState: WorkflowProcessConfigState;
  sx?: SxProps<Theme>;
  onEdit: (editProps: WorkflowEditProps) => void;
  orgUnits: OrganizationUnit[];
};
export const WorkflowSummary = (props: PropsType) => {
  const { t } = useTranslation();
  const { workflowConfig, workflowState, sx, onEdit, orgUnits } = props;

  const steps = workflowConfig.workflow.approvalSteps;
  const showTarget = workflowConfig.workflow.initiators.roleNames.includes(
    WorkflowRoles.MANAGER,
  );
  const showFirstApprover =
    workflowConfig.workflow.initiators.roleNames.includes(
      WorkflowRoles.FIRST_APPROVER,
    );

  const sxStyles = useSxStyles({
    stepsCount: steps.length + (showTarget ? 2 : 1),
    showTarget,
  });

  const getUnitLabel = useCallback(
    (unitId: number) => {
      const unit = orgUnits.find((unit) => unit.fOrgUnitId === unitId);

      return !!unit ? getOrgUnitLabel(unit) : unitId;
    },
    [orgUnits],
  );

  const stateInfo = useMemo(() => {
    switch (workflowState) {
      case WorkflowProcessConfigState.Unit:
        return t('unitWorkflowSummaryDescription');
      case WorkflowProcessConfigState.Inherited:
        return (
          <>
            {t('inheritedWorkflowSummaryDescription')}{' '}
            <b>{getUnitLabel(workflowConfig.unitId)}</b>
          </>
        );
      default:
        return (
          <Box className={'createWorkflow'}>
            <Box className={'createWorkflow-content'}>
              <Box>{t('noWorkflowConfigured')}</Box>
              <Box>
                <Button
                  onClick={() =>
                    onEdit({
                      workflowConfig: workflowConfig,
                      isEdit: false,
                    })
                  }
                  startIcon={<Add />}
                >
                  {t('createWorkflow')}
                </Button>
              </Box>
            </Box>
          </Box>
        );
    }
  }, [workflowState, workflowConfig, onEdit, getUnitLabel, t]);

  const ApprovalStep = (stepProps: {
    stepConfig: WorkflowApprovalStepConfig;
    finalStep?: boolean;
    showFirstApprover?: boolean;
  }) => {
    const { stepConfig, finalStep } = stepProps;

    const isInitiator = stepConfig.seq === 0;
    const isTarget = stepConfig.seq === -1;
    const stepLabel = (seq: number) => {
      switch (seq) {
        case -1:
          return t('workflowTarget');
        case 0:
          return t('workflowInitiator');
        default:
          return seq;
      }
    };

    return (
      <Box className={'stepArea'}>
        <Box
          className={classNames(
            'stepBox',
            finalStep && 'final',
            isInitiator && 'init',
            isTarget && 'target',
          )}
        >
          <Box className={'stepNumber'}>{stepLabel(stepConfig.seq)}</Box>
          <Box className={'stepContent'}>
            {stepConfig.roleNames.map((roleName) => {
              return (
                <Box key={`stepRole-${roleName}`} className={'stepRole'}>
                  {t(getWorkflowRolesTranslationKeyFromString(roleName))}
                </Box>
              );
            })}
            {stepConfig.seq === 1 && showFirstApprover && (
              <Box className={'relativeRole'}>{t('firstApprover')}</Box>
            )}
            {finalStep && (
              <Box className={'relativeRole'}>{t('finalApprover')}</Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ ...sxStyles.root, ...sx }}>
      <Box className={'stateInfo'}>{stateInfo}</Box>

      {steps.length > 0 && (
        <Box className={'stepsRoot'}>
          <Box className={'stepsSet'}>
            {showTarget && (
              <ApprovalStep
                stepConfig={{
                  roleNames: [WorkflowRoles.EMPLOYEE],
                  seq: -1,
                }}
              />
            )}
            <ApprovalStep
              stepConfig={{
                ...workflowConfig.workflow.initiators,
                seq: 0,
              }}
            />
            {steps.map((step) => {
              const finalStep = steps.length === step.seq;

              return (
                <ApprovalStep
                  stepConfig={step}
                  finalStep={finalStep}
                  showFirstApprover={showFirstApprover}
                  key={`step-${step.seq}`}
                />
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
};
